import {
  Component, Input, ViewChild, ElementRef,
  AfterViewInit, OnChanges, OnDestroy, OnInit
} from '@angular/core';
import { echarts, ECharts, EChartsCoreOption } from '../echarts.config';
import type { BarSeriesOption } from 'echarts/charts';


@Component({
  selector: 'app-chart-grouped-column',
  templateUrl: './chart-grouped-column.component.html',
  styleUrl: './chart-grouped-column.component.scss',
  standalone: false
})
export class ChartGroupedColumnComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() categories: string[] = [];
  @Input() seriesData: any[] = [];

  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() yAxisTitle: string = '';
  @Input() stackLabelsEnabled: boolean = true;

  @ViewChild('container', { static: true }) container!: ElementRef;

  private chart: ECharts | null = null;
  private themeObserver!: MutationObserver;

  ngOnInit(): void {
    const html = document.documentElement;
    const body = document.body;

    if (html.classList.contains('app-dark')) {
      body.classList.add('app-dark');
    } else {
      body.classList.remove('app-dark');
    }

    this.themeObserver = new MutationObserver(() => {
      if (html.classList.contains('app-dark')) {
        body.classList.add('app-dark');
      } else {
        body.classList.remove('app-dark');
      }
      this.chart?.dispose();
      this.initChart();
    });

    this.themeObserver.observe(html, { attributes: true, attributeFilter: ['class'] });
  }

  ngAfterViewInit(): void {
    this.initChart();
  }

  ngOnChanges(): void {
    if (this.chart) {
      this.updateChart();
    }
  }

  ngOnDestroy(): void {
    this.themeObserver?.disconnect();
    this.chart?.dispose();
  }

  private get isDark(): boolean {
    return document.body.classList.contains('app-dark');
  }

  private initChart(): void {
    this.container.nativeElement.innerHTML = '';

    const theme = this.isDark ? 'dark' : undefined;
    this.chart = echarts.init(this.container.nativeElement, theme);
    this.chart.setOption(this.buildOption());

    setTimeout(() => this.chart?.resize(), 0);
  }

  private updateChart(): void {
    if (!this.chart) return;
    this.chart.setOption(this.buildOption(), { replaceMerge: ['series'] });
  }

  private buildOption(): EChartsCoreOption {
    const isDark = this.isDark;
    const textColor = isDark ? '#e0e0e0' : '#333';
    const axisLineColor = isDark ? '#555' : '#ccc';

    return {
      backgroundColor: 'transparent',
      title: {
        text: this.title,
        subtext: this.subtitle,
        left: 'center',
        textStyle: { color: textColor }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: {
        top: 0,
        right: 0,
        textStyle: { color: textColor }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: this.categories,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisLabel: {
          color: textColor,
          rotate: 0  // equivalente a autoRotation: [0]
        },
        axisTick: { alignWithLabel: true }
      },
      yAxis: {
        type: 'value',
        name: this.yAxisTitle,
        nameLocation: 'middle',
        nameGap: 40,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisLabel: { color: textColor },
        splitLine: { lineStyle: { color: axisLineColor, type: 'dashed' } }
      },
      series: this.getSeries()
    };
  }

  private getSeries(): BarSeriesOption[] {
    return this.seriesData.map((item) => ({
      name: item.name,
      type: 'bar',
      // Sin stack → grouped automáticamente
      data: item.values,
      ...(item.color ? { color: item.color } : {}),
      barMaxWidth: 55,
      itemStyle: {
        borderRadius: [4, 4, 0, 0]
      },
      label: {
        show: true,
        position: 'top',
        formatter: (params: any) => `${params.value}%`,
        fontSize: 12,
      },
      emphasis: { focus: 'series' }
    }));
  }
}